/**
 * useAdminLogs.ts
 * 
 * Custom hook for managing admin logs data and state.
 * Moves ALL business logic from components to this hook.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { adminLogsApi } from '../services/adminApi'

interface LogFilter {
  level?: 'info' | 'warning' | 'error' | 'critical'
  category?: string
  dateRange?: {
    start: string
    end: string
  }
  search?: string
}

interface LogEntry {
  id: string
  level: 'info' | 'warning' | 'error' | 'critical'
  message: string
  timestamp: string
  category: string
  userId?: string
  metadata?: Record<string, any>
}

interface UseAdminLogsReturn {
  // State
  logs: LogEntry[]
  loading: boolean
  error: string | null
  filters: LogFilter
  selectedLog: LogEntry | null
  showDetailModal: boolean
  
  // Computed values
  filteredLogs: LogEntry[]
  logStats: {
    total: number
    info: number
    warning: number
    error: number
    critical: number
  }
  
  // Actions
  setFilters: (filters: Partial<LogFilter>) => void
  clearFilters: () => void
  selectLog: (log: LogEntry | null) => void
  openDetailModal: () => void
  closeDetailModal: () => void
  exportLogs: (format: 'csv' | 'json') => Promise<void>
  deleteLogs: (logIds: string[]) => Promise<void>
  refreshLogs: () => Promise<void>
}

export const useAdminLogs = (): UseAdminLogsReturn => {
  // State
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFiltersState] = useState<LogFilter>({})
  const [selectedLog, setSelectedLog] = useState<LogEntry | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)

  // Load logs
  const loadLogs = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await adminLogsApi.getLogs()
      setLogs(response.logs || [])
    } catch (err) {
      setError('Failed to load logs')
      console.error('Error loading logs:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  // Filter logs based on current filters
  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      // Level filter
      if (filters.level && log.level !== filters.level) {
        return false
      }
      
      // Category filter
      if (filters.category && log.category !== filters.category) {
        return false
      }
      
      // Date range filter
      if (filters.dateRange) {
        const logDate = new Date(log.timestamp)
        const startDate = new Date(filters.dateRange.start)
        const endDate = new Date(filters.dateRange.end)
        
        if (logDate < startDate || logDate > endDate) {
          return false
        }
      }
      
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase()
        const messageLower = log.message.toLowerCase()
        const categoryLower = log.category.toLowerCase()
        
        if (!messageLower.includes(searchLower) && !categoryLower.includes(searchLower)) {
          return false
        }
      }
      
      return true
    })
  }, [logs, filters])

  // Calculate log statistics
  const logStats = useMemo(() => {
    return {
      total: logs.length,
      info: logs.filter(log => log.level === 'info').length,
      warning: logs.filter(log => log.level === 'warning').length,
      error: logs.filter(log => log.level === 'error').length,
      critical: logs.filter(log => log.level === 'critical').length
    }
  }, [logs])

  // Actions
  const setFilters = useCallback((newFilters: Partial<LogFilter>) => {
    setFiltersState(prev => ({ ...prev, ...newFilters }))
  }, [])

  const clearFilters = useCallback(() => {
    setFiltersState({})
  }, [])

  const selectLog = useCallback((log: LogEntry | null) => {
    setSelectedLog(log)
  }, [])

  const openDetailModal = useCallback(() => {
    setShowDetailModal(true)
  }, [])

  const closeDetailModal = useCallback(() => {
    setShowDetailModal(false)
    setSelectedLog(null)
  }, [])

  const exportLogs = useCallback(async (format: 'csv' | 'json') => {
    try {
      setLoading(true)
      await adminLogsApi.exportLogs({ format, filters })
      // Handle success (e.g., show notification)
    } catch (err) {
      setError('Failed to export logs')
      console.error('Error exporting logs:', err)
    } finally {
      setLoading(false)
    }
  }, [filters])

  const deleteLogs = useCallback(async (logIds: string[]) => {
    try {
      setLoading(true)
      await adminLogsApi.deleteLogs()
      // Remove deleted logs from state
      setLogs(prev => prev.filter(log => !logIds.includes(log.id)))
    } catch (err) {
      setError('Failed to delete logs')
      console.error('Error deleting logs:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const refreshLogs = useCallback(async () => {
    await loadLogs()
  }, [loadLogs])

  // Load logs on mount
  useEffect(() => {
    loadLogs()
  }, [loadLogs])

  return {
    // State
    logs,
    loading,
    error,
    filters,
    selectedLog,
    showDetailModal,
    
    // Computed values
    filteredLogs,
    logStats,
    
    // Actions
    setFilters,
    clearFilters,
    selectLog,
    openDetailModal,
    closeDetailModal,
    exportLogs,
    deleteLogs,
    refreshLogs
  }
} 